import {
  Box,
  Flex,
  Heading,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Icon,
  useColorModeValue,
  Text,
  Stack,
  Tabs,
  Tab,
  TabList,
  TabPanels,
  TabPanel,
  Center,
  Spinner,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { FaUsers, FaCoins, FaGift, FaStamp, FaArrowUp, FaArrowDown, FaMinus, FaChartLine } from 'react-icons/fa';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';
import AdminHero from "../../components/AdminComponents/AdminHero";
import { useSelector } from 'react-redux';
import React, { useEffect, useState } from 'react';
import { useFetchAdminDashboardStatsMutation, useFetchTodaysEarningsMutation, useFetchTodaysStampsEarningsMutation} from '../../slices/adminApiSlice';
import Loader from '../../components/Loader';
import { TransactionTables } from '../../components/AdminComponents/TransactionTables';

const Dashboard = () => {
  const { adminInfo } = useSelector((state) => state.adminAuth);
  const bgCard = useColorModeValue('white', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');
  const barStampColor = useColorModeValue('#38A169', '#68D391');
  const statLabelTextColor = useColorModeValue('gray.600', 'gray.300');
  const tooltipBg = useColorModeValue("gray.50", "gray.800");
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const emptyStateColor = useColorModeValue('gray.500', 'gray.400');

  // Stamps Chart Color
  const textColor2 = useColorModeValue('#2D3748', '#CBD5E0');
  const tooltipBg2 = useColorModeValue('white', '#2D3748');
  const tooltipTextColor2 = useColorModeValue('#2D3748', '#E2E8F0');
  const borderColor2 = useColorModeValue('#E2E8F0', '#4A5568');
  const barStampColor2 = useColorModeValue('#3182CE', '#63B3ED');
  const gridColor2 = useColorModeValue('#E2E8F0', '#4A5568');

  const [getStats, { data: stats, isLoading, error }] = useFetchAdminDashboardStatsMutation();
  const [getTodaysEarnings, { data: todaysEarnings, isTodayEarningLoading, todaysEarningError }] = useFetchTodaysEarningsMutation();
  const [getTodaysStampsEarnings, { data: todaysStampsEarnings, isTodayStampsEarningLoading, todaysStampsEarningError }] = useFetchTodaysStampsEarningsMutation();
  const [selectedTabIndex, setSelectedTabIndex] = useState(0); // 0 = Points, 1 = Stamps
  const [range, setRange] = useState('today');

  useEffect(() => {
    getStats();
    getTodaysEarnings(range);
    getTodaysStampsEarnings(range);
  }, [getStats, getTodaysEarnings, range, getTodaysStampsEarnings]);

  const stampChartData = (todaysStampsEarnings?.data || []).map((user) => ({
    name: user.userName,
    stamps: user.stamps,
  }));

  const chartData = (todaysEarnings?.data || []).map((user) => ({
    name: user.userName,
    points: user.points,
  }));

  const EmptyState = ({ icon, title, description }) => (
    <Center flexDirection="column" py={10} px={4} textAlign="center">
      <Icon as={icon} boxSize={8} mb={4} color="gray.400" />
      <Heading size="sm" mb={2} color={textColor}>
        {title}
      </Heading>
      <Text color={emptyStateColor}>
        {description}
      </Text>
    </Center>
  );

  // StatCard component example
  const StatCard = ({ label, value, icon, colorScheme, subValue, subValue2, trend }) => {
    const trendColor = {
      up: 'green.500',
      down: 'red.500',
      flat: 'gray.500'
    };
    
    const trendIcon = {
      up: FaArrowUp,
      down: FaArrowDown,
      flat: FaMinus
    };
    
    return (
      <Box
        bg={bgCard}
        p={6}
        borderRadius="xl"
        boxShadow="sm"
        borderWidth="1px"
        borderColor={borderColor}
      >
        <Stat>
          <Flex align="center">
            <Center
              mr={4}
              w={12}
              h={12}
              borderRadius="lg"
              bg={`${colorScheme}.100`}
              color={`${colorScheme}.500`}
            >
              <Icon as={icon} boxSize={6} />
            </Center>
            <Box>
              <StatLabel color={statLabelTextColor}>
                {label}
              </StatLabel>
              <StatNumber fontSize="2xl" fontWeight="bold">
                {value.toLocaleString()}
                {trend && (
                  <Icon
                    as={trendIcon[trend]}
                    ml={2}
                    color={trendColor[trend]}
                    boxSize={4}
                  />
                )}
              </StatNumber>
              <Box mt={1}>
                {subValue && (
                  <Flex align="center">
                    <Text fontSize="sm" color={emptyStateColor}>
                      {subValue}
                    </Text>
                    {label === 'Rewards Redeemed' && trend && (
                      <Icon
                        as={trendIcon[trend]}
                        ml={2}
                        color={trendColor[trend]}
                        boxSize={2}
                      />
                    )}
                  </Flex>
                )}
                {subValue2 && (
                  <Flex align="center">
                    <Text fontSize="sm" color={emptyStateColor}>
                      {subValue2}
                    </Text>
                    {label === 'Rewards Redeemed' && trend && (
                      <Icon
                        as={trendIcon[trend]}
                        ml={2}
                        color={trendColor[trend]}
                        boxSize={2}
                      />
                    )}
                  </Flex>
                )}
              </Box>
            </Box>
          </Flex>
        </Stat>
      </Box>
    );
  };

  const getRangeLabel = (range) => {
    switch (range) {
      case 'week':
        return 'This Week’s';
      case 'month':
        return 'This Month’s';
      case 'year':
        return 'This Year’s';
      default:
        return 'Today’s';
    }
  };

  return (
    <>
      {adminInfo ? (
        <Box p={5} bg={tooltipBg}>
          {/* Handle loading & error states */}
          {isLoading && <Loader />}
          {error && (
            <Alert status="error" mb={6} borderRadius="lg">
              <AlertIcon />
              <Text>Error fetching stats: {error.message}</Text>
            </Alert>
          )}
  
          {/* Stats Grid */}
          {stats && (
            <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={6} mb={8}>
              {[
                { 
                  label: 'Total Users', 
                  value: stats.totalUsers, 
                  icon: FaUsers, 
                  colorScheme: 'blue',
                  trend: stats.userGrowth
                },
                { 
                  label: 'Total Points', 
                  value: stats.points.earned, 
                  icon: FaCoins, 
                  colorScheme: 'teal',
                  subValue: `Redeemed: ${Math.abs(stats.points.redeemed).toLocaleString()}`,
                  subValue2: `Expired: ${Math.abs(stats.points.expired).toLocaleString()}`,
                  trend: stats.points.trend
                },
                { 
                  label: 'Total Stamps', 
                  value: stats.stamps.earned, 
                  icon: FaStamp, 
                  colorScheme: 'orange',
                  subValue: `Redeemed: ${Math.abs(stats.stamps.redeemed).toLocaleString()}`,
                  subValue2: `Expired: ${Math.abs(stats.stamps.expired).toLocaleString()}`,
                  trend: stats.stamps.trend
                },
                { 
                  label: 'Rewards Redeemed', 
                  value: stats.rewardsRedeemed.totalRewardsCount, 
                  icon: FaGift, 
                  colorScheme: 'purple',
                  subValue: `Points Rewards: ${stats.rewardsRedeemed.points}`,
                  subValue2: `Stamps Rewards: ${stats.rewardsRedeemed.stamps}`,
                  trend: stats.rewardsRedeemed.overallRewardsTrend
                },
              ].map((stat, index) => (
                <StatCard key={index} {...stat} />
              ))}
            </SimpleGrid>
          )}
  
          {/* Main Content */}
          <Stack spacing={6} direction={{ base: "column", lg: "row" }} align="stretch">
            {/* Activity Chart */}
            <Box 
              flex="1" 
              bg={bgCard} 
              p={6} 
              borderRadius="xl" 
              boxShadow="md"
              borderWidth="1px"
              borderColor={borderColor}
            >

              {/* <Heading as="h3" size="md" mb={6} color={textColor} display="flex" alignItems="center">
                <Icon as={FaChartLine} mr={2} /> Today's Activity
              </Heading> */}
              <Heading
                as="h3"
                size="md"
                mb={6}
                color={textColor}
                display="flex"
                alignItems="center"
              >
                <Icon as={FaChartLine} mr={2} />
                {getRangeLabel(range)} Activity Chart
              </Heading>
              
              <Tabs 
                index={selectedTabIndex}
                onChange={setSelectedTabIndex}
                variant="soft-rounded" 
                colorScheme="teal"
              >
                <TabList mb={6}>
                  <Tab>
                    <Flex align="center">
                      <Icon as={FaCoins} mr={2} /> Points
                    </Flex>
                  </Tab>
                  <Tab>
                    <Flex align="center">
                      <Icon as={FaStamp} mr={2} /> Stamps
                    </Flex>
                  </Tab>
                </TabList>
                
                <TabPanels>
                  <TabPanel p={0}>
                    {isTodayEarningLoading ? (
                      <Center h="300px"><Spinner size="xl" /></Center>
                    ) : todaysEarningError ? (
                      <Alert status="error" borderRadius="md">
                        <AlertIcon />
                        <Text>Error loading points data</Text>
                      </Alert>
                    ) : todaysEarnings?.data?.length > 0 ? (
                      <Box h="400px" bg={bgCard} borderRadius="xl" boxShadow="md" p={4}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={chartData} barCategoryGap="20%" barGap={4}>
                            <CartesianGrid strokeDasharray="3 3" stroke={gridColor2} />
                            <XAxis 
                              dataKey="name" 
                              tick={{ fill: textColor2, fontSize: 12 }}
                              tickLine={false}
                              axisLine={{ stroke: borderColor2 }}
                            />
                            <YAxis 
                              tick={{ fill: textColor2, fontSize: 12 }}
                              tickLine={false}
                              axisLine={{ stroke: borderColor2 }}
                            />
                            <Tooltip 
                              contentStyle={{
                                backgroundColor: tooltipBg2,
                                color: tooltipTextColor2,
                                borderRadius: '8px',
                                border: `1px solid ${borderColor2}`,
                                fontSize: '13px',
                              }}
                              cursor={{ fill: gridColor2, opacity: 0.1 }}
                            />
                            <Bar 
                              dataKey="points" 
                              fill={barStampColor}
                              radius={[8, 8, 0, 0]} 
                              animationDuration={1000}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    ) : (
                      <EmptyState 
                        icon={FaCoins}
                        title="No points activity today"
                        description="No points were earned or redeemed today"
                      />
                    )}
                  </TabPanel>
                  
                  <TabPanel p={0}>
                    {isTodayStampsEarningLoading ? (
                      <Center h="300px"><Spinner size="xl" /></Center>
                    ) : todaysStampsEarningError ? (
                      <Alert status="error" borderRadius="md">
                        <AlertIcon />
                        <Text>Error loading stamps data</Text>
                      </Alert>
                    ) : todaysStampsEarnings?.data?.length > 0 ? (
                      <Box h="400px" bg={bgCard} borderRadius="xl" boxShadow="md" p={4}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={stampChartData} barCategoryGap="20%" barGap={2}>
                            <CartesianGrid strokeDasharray="4 4" stroke={gridColor2} />
                            
                            <XAxis 
                              dataKey="name" 
                              tick={{ fill: textColor2, fontSize: 12 }}
                              tickLine={false}
                              axisLine={{ stroke: borderColor2 }}
                            />

                            <YAxis 
                              tick={{ fill: textColor2, fontSize: 12 }}
                              tickLine={false}
                              axisLine={{ stroke: borderColor2 }}
                            />

                            <Tooltip 
                              contentStyle={{
                                backgroundColor: tooltipBg2,
                                color: tooltipTextColor2,
                                borderRadius: '8px',
                                border: `1px solid ${borderColor2}`,
                                fontSize: '13px',
                              }}
                              cursor={{ fill: gridColor2, opacity: 0.1 }}
                            />

                            <Bar 
                              dataKey="stamps" 
                              fill={barStampColor2}
                              radius={[8, 8, 0, 0]} 
                              animationDuration={1000}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>

                    ) : (
                      <EmptyState 
                        icon={FaStamp}
                        title="No stamps activity today"
                        description="No stamps were earned or redeemed today"
                      />
                    )}
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </Box>
  
            {/* Transactions Section */}
            <Box  
              flex="1"
              bg={bgCard} 
              p={6} 
              borderRadius="xl" 
              boxShadow="md"
              borderWidth="1px"
              borderColor={borderColor}
              >
              <Heading
                as="h3"
                size="md"
                mb={6}
                color={textColor}
                display="flex"
                alignItems="center"
              >
                <Icon as={FaChartLine} mr={2} />
                {getRangeLabel(range)} Transactions Overview
              </Heading>

              <Tabs 
                index={selectedTabIndex}
                onChange={setSelectedTabIndex}
                variant="soft-rounded" 
                colorScheme="teal"
              >
                <TabList mb={6}>
                  <Tab>
                    <Flex align="center">
                      <Icon as={FaCoins} mr={2} /> Points
                    </Flex>
                  </Tab>
                  <Tab>
                    <Flex align="center">
                      <Icon as={FaStamp} mr={2} /> Stamps
                    </Flex>
                  </Tab>
                </TabList>
                
                <TabPanels>
                  <TabPanel p={0}>
                    <TransactionTables 
                      pointsData={todaysEarnings} 
                      loading={isTodayEarningLoading}
                      error={todaysEarningError}
                      selectedRange={range}
                      onRangeChange={setRange}
                    />
                  </TabPanel>
                  
                  <TabPanel p={0}>
                    <TransactionTables 
                      stampsData={todaysStampsEarnings} 
                      loading={isTodayStampsEarningLoading}
                      error={todaysStampsEarningError}
                      isStamps
                      selectedRange={range}
                      onRangeChange={setRange}
                    />
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </Box>
          </Stack>
        </Box>
      ) : (
        <AdminHero />
      )}
    </>
  );
};

export default Dashboard;
